// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Shunt — the par solver. Pure, deterministic, no clock.
//
// WHY THIS EXISTS AT ALL. The obvious way to build a shunting puzzle is to walk BACKWARDS from a
// finished train, shuffling cars out into the sidings, and call the length of that walk the par.
// That is wrong, and quietly so: a backwards walk yields A solution, never THE shortest one. A
// 14-move walk routinely lands on a yard that solves in 9, so every results screen would tell
// every player they were "5 over par" when they had in fact beaten it. The results screen in
// this game promises to say exactly how far off the optimum each player was, so par has to BE
// the optimum.
//
// So generation and par are the SAME computation, run once per yard: a Dijkstra outward from the
// set of finished positions. Because PUSH and PULL are exact inverses (see rules.ts) the move
// graph is undirected with symmetric costs, so the distance FROM a finished position TO a yard is
// identical to the distance from that yard back to a finished one. That gives, in one pass:
//
//   • the exact optimal move count for every position it reaches — par, proven, not estimated;
//   • a supply of start positions at any difficulty we ask for, every one guaranteed solvable;
//   • the optimal line itself, by walking downhill through the distance map — which is what the
//     results screen replays when it shows players the line they missed.
//
// Costs are 1 (push/pull) and 2 (run-round), so this is Dial's algorithm — a bucket per distance,
// drained in ascending order — rather than a plain BFS queue. A binary heap would work too and be
// slower for a weight set this small.

import {
  applyMove,
  carsIn,
  childrenOf,
  cloneYard,
  isSolved,
  keyOf,
  legalMoves,
  moveCost,
  ROOT,
  type Move,
  type Yard,
  type YardSpec,
} from './rules';

export interface SearchOpts {
  /** Stop expanding past this distance. States beyond it are never used, so this bounds the work. */
  readonly maxDist: number;
  /** Hard ceiling on expanded states, so a pathological yard cannot hang a phone. */
  readonly maxNodes: number;
}

export interface SearchResult {
  /** Exact optimal move count from each reached position to a finished train. */
  readonly dist: Map<string, number>;
  /** Position keys bucketed by that distance — byDist[d] is every yard that solves in exactly d. */
  readonly byDist: string[][];
  /** True if the node ceiling stopped the search early (the distances found are still exact). */
  readonly truncated: boolean;
  /** How many positions were expanded — the number the timing tests watch. */
  readonly expanded: number;
}

/** Rebuild a yard from `keyOf`. The key is the whole position, so this is lossless. */
export function parseKey(spec: YardSpec, target: readonly number[], key: string): Yard {
  const parts = key.split('|');
  const locoAt = Number(parts[0]);
  const cars: number[][] = [];
  for (let t = 0; t < spec.tracks.length; t++) {
    const road = parts[t + 1] ?? '';
    const list: number[] = [];
    for (let i = 0; i < road.length; i++) list.push(road.charCodeAt(i) - 65);
    cars.push(list);
  }
  return { spec, cars, locoAt, target };
}

/**
 * Every finished position: the departure road holds the ordered train, and the cars that are not
 * part of it stand anywhere else that is legal, with the loco on any road.
 *
 * Filtered by the yard invariant that the parent of the loco's road is always empty — the loco
 * only ever reaches a road by propelling into it (which empties the road it came from), so a
 * position that breaks this is not reachable from any start and would only waste search budget.
 */
export function finishedPositions(spec: YardSpec, target: readonly number[], allCars: readonly number[]): Yard[] {
  const spares = allCars.filter((c) => !target.includes(c));
  const roads: number[] = [];
  for (let i = 0; i < spec.tracks.length; i++) {
    if (i !== spec.departure) roads.push(i);
  }

  const out: Yard[] = [];
  const cars: number[][] = spec.tracks.map(() => []);
  cars[spec.departure] = target.slice();

  const walk = (pool: number[], roadIdx: number): void => {
    if (roadIdx >= roads.length) {
      if (pool.length > 0) return;
      for (let loco = 0; loco < spec.tracks.length; loco++) {
        const parent = spec.tracks[loco].parent;
        if (parent !== null && cars[parent].length > 0) continue;
        out.push({ spec, cars: cars.map((r) => r.slice()), locoAt: loco, target });
      }
      return;
    }
    const road = roads[roadIdx];
    const cap = spec.tracks[road].cap;
    const maxHere = Math.min(cap, pool.length);
    for (let take = 0; take <= maxHere; take++) {
      eachOrderedSelection(pool, take, (chosen, rest) => {
        cars[road] = chosen;
        walk(rest, roadIdx + 1);
        cars[road] = [];
      });
    }
  };
  walk(spares.slice(), 0);
  return out;
}

/** Every ordered selection of exactly `take` items from `pool`, with the leftovers. Deterministic. */
function eachOrderedSelection(pool: number[], take: number, fn: (chosen: number[], rest: number[]) => void): void {
  if (take === 0) {
    fn([], pool.slice());
    return;
  }
  for (let i = 0; i < pool.length; i++) {
    const head = pool[i];
    const rest = pool.slice(0, i).concat(pool.slice(i + 1));
    eachOrderedSelection(rest, take - 1, (chosen, leftover) => fn([head, ...chosen], leftover));
  }
}

/**
 * Dijkstra outward from every finished position, up to `maxDist`, giving the exact optimal move
 * count for every position it reaches.
 */
export function searchFromFinished(
  spec: YardSpec,
  target: readonly number[],
  allCars: readonly number[],
  opts: SearchOpts,
): SearchResult {
  const dist = new Map<string, number>();
  const byDist: string[][] = [];
  for (let d = 0; d <= opts.maxDist; d++) byDist.push([]);

  const buckets: string[][] = [];
  for (let d = 0; d <= opts.maxDist; d++) buckets.push([]);

  for (const g of finishedPositions(spec, target, allCars)) {
    const k = keyOf(g);
    if (!dist.has(k)) {
      dist.set(k, 0);
      buckets[0].push(k);
    }
  }

  let expanded = 0;
  let truncated = false;

  for (let d = 0; d <= opts.maxDist && !truncated; d++) {
    const bucket = buckets[d];
    for (let bi = 0; bi < bucket.length; bi++) {
      const key = bucket[bi];
      if (dist.get(key) !== d) continue; // superseded by a shorter route
      byDist[d].push(key);
      if (++expanded > opts.maxNodes) {
        truncated = true;
        break;
      }
      const y = parseKey(spec, target, key);
      for (const m of legalMoves(y)) {
        const nd = d + moveCost(m);
        if (nd > opts.maxDist) continue;
        const next = cloneYard(y);
        applyMove(next, m);
        const nk = keyOf(next);
        const seen = dist.get(nk);
        if (seen === undefined || seen > nd) {
          dist.set(nk, nd);
          buckets[nd].push(nk);
        }
      }
    }
  }

  return { dist, byDist, truncated, expanded };
}

/**
 * The optimal line from a position, recovered by walking downhill through a distance map. Returns
 * null if the position is not in the map. This is what the results screen replays.
 */
export function optimalLine(y: Yard, dist: Map<string, number>): Move[] | null {
  let cur = cloneYard(y);
  let d = dist.get(keyOf(cur));
  if (d === undefined) return null;
  const line: Move[] = [];
  let guard = 0;
  while (d > 0 && guard++ < 200) {
    let best: { m: Move; nd: number } | null = null;
    for (const m of legalMoves(cur)) {
      const next = cloneYard(cur);
      applyMove(next, m);
      const nd = dist.get(keyOf(next));
      if (nd === undefined) continue;
      if (nd + moveCost(m) === d && (best === null || nd < best.nd)) best = { m, nd };
    }
    if (!best) return null;
    line.push(best.m);
    applyMove(cur, best.m);
    d = best.nd;
  }
  return isSolved(cur) ? line : null;
}

/**
 * An INDEPENDENT check that a claimed par really is optimal: a plain breadth-first (Dial) search
 * from the position itself until a finished yard falls out. It shares rules.ts, deliberately
 * shares nothing else with `searchFromFinished`, and is what tests/solve.test.ts holds the
 * generator to (principle: an audit that reuses the thing it audits proves nothing).
 */
export function optimalParDirect(y: Yard, maxDist: number, maxNodes = 400_000): number | null {
  const start = keyOf(y);
  if (isSolved(y)) return 0;
  const seen = new Map<string, number>([[start, 0]]);
  const buckets: string[][] = [];
  for (let d = 0; d <= maxDist; d++) buckets.push([]);
  buckets[0].push(start);
  let expanded = 0;

  for (let d = 0; d <= maxDist; d++) {
    const bucket = buckets[d];
    for (let bi = 0; bi < bucket.length; bi++) {
      const key = bucket[bi];
      if (seen.get(key) !== d) continue;
      if (++expanded > maxNodes) return null;
      const cur = parseKey(y.spec, y.target, key);
      for (const m of legalMoves(cur)) {
        const nd = d + moveCost(m);
        if (nd > maxDist) continue;
        const next = cloneYard(cur);
        applyMove(next, m);
        if (isSolved(next)) return nd;
        const nk = keyOf(next);
        const was = seen.get(nk);
        if (was === undefined || was > nd) {
          seen.set(nk, nd);
          buckets[nd].push(nk);
        }
      }
    }
  }
  return null;
}

/** The yard invariant the model depends on: the road a loco stands on has an empty parent. */
export function parentIsClear(y: Yard): boolean {
  const parent = y.spec.tracks[y.locoAt].parent;
  return parent === null || y.cars[parent].length === 0;
}

/** Sanity helpers used by the tests and the generator. */
export function allCarsOf(y: Yard): number[] {
  return carsIn(y);
}

export function rootChildren(spec: YardSpec): number[] {
  return childrenOf(spec, ROOT);
}
